import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, ArrowRight, CheckCircle, Scale, Clock, AlertCircle } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { SharedNav, SharedFooter, Reveal } from '../components/SharedLayout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ServicesPage = () => {
  const { lang } = useLanguage();
  useEffect(() => { axios.post(`${API}/track`, { event_type: 'page_view', page: '/services' }).catch(() => {}); }, []);

  const services = [
    { title: lang === 'es' ? 'Fianzas de Garantia' : 'Surety Bonds', desc: lang === 'es' ? 'El tipo mas comun. Lone Star publica el monto completo y usted paga solo un porcentaje.' : 'The most common type. Lone Star posts the full bail amount and you pay only a percentage fee.', badge: lang === 'es' ? 'Mas Comun' : 'Most Common' },
    { title: lang === 'es' ? 'Fianzas en Efectivo' : 'Cash Bonds', desc: lang === 'es' ? 'Para acusados que prefieren pagar la fianza directamente en efectivo.' : 'For defendants who prefer to post cash bail directly. We guide you through the process.', badge: lang === 'es' ? 'Liberacion Rapida' : 'Fast Release' },
    { title: lang === 'es' ? 'Fianzas DUI' : 'DUI / Traffic Bonds', desc: lang === 'es' ? 'Especializados en fianzas DUI y delitos de trafico. Decadas de experiencia.' : 'Specialized in DUI and traffic offense bonds. Decades of Harris County experience.', badge: lang === 'es' ? 'Especializado' : 'Specialized' },
    { title: lang === 'es' ? 'Fianzas por Delito Mayor' : 'Felony Bonds', desc: lang === 'es' ? 'Fianzas de alto monto manejadas con discrecion y rapidez.' : 'High-amount felony bonds handled with discretion and speed.', badge: lang === 'es' ? 'Alto Riesgo' : 'High-Stakes' },
    { title: lang === 'es' ? 'Fianzas por Delito Menor' : 'Misdemeanor Bonds', desc: lang === 'es' ? 'Respuesta rapida para casos menores. Liberacion en horas.' : 'Quick turnaround for misdemeanor cases. Released within hours.', badge: lang === 'es' ? 'Rapida' : 'Quick' },
    { title: lang === 'es' ? 'Fianzas Federales' : 'Federal Bonds', desc: lang === 'es' ? 'Fianzas en tribunales federales manejadas por profesionales experimentados.' : 'Federal court bail bonds handled by experienced professionals.', badge: lang === 'es' ? 'Federales' : 'Federal' },
  ];

  return (
    <>
      <Helmet>
        <title>Houston Bail Bond Services | Surety, Felony, DUI, Federal Bonds | Lone Star Bonding Co.</title>
        <meta name="description" content="Lone Star Bonding Co. handles all types of bail bonds in Houston: Surety, Cash, DUI, Felony, Misdemeanor, and Federal bonds. 24/7 service. 30+ years experience. Call (713) 228-8800." />
        <link rel="canonical" href="https://lonestarbonding.com/services" />
      </Helmet>
      <SharedNav />

      <section style={{ paddingTop: '10rem', paddingBottom: '4rem', background: 'var(--void)', textAlign: 'center' }}>
        <div className="container" style={{ maxWidth: 800 }}>
          <Reveal>
            <span className="section-header__label">{lang === 'es' ? 'NUESTROS SERVICIOS' : 'OUR SERVICES'}</span>
            <h1 className="section-header__title" style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)' }}>
              {lang === 'es' ? 'SOLUCIONES DE FIANZA' : 'BAIL BOND SOLUTIONS'}
            </h1>
            <p className="section-header__subtitle">
              {lang === 'es' ? 'No todos los clientes llegan con un caso simple. Manejamos una amplia gama de situaciones.' : 'Not every client walks in with a simple case. We handle a wide range of bond situations.'}
            </p>
          </Reveal>
        </div>
      </section>

      <section style={{ padding: '4rem 2rem', background: 'var(--void)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {services.map((s, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="authority-card" data-testid={`service-card-${i}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <Scale size={24} style={{ color: 'var(--astros-orange)' }} />
                    <span style={{ background: 'rgba(235, 110, 31, 0.08)', border: '1px solid rgba(235, 110, 31, 0.2)', padding: '0.2rem 0.6rem', fontSize: '0.7rem', color: 'var(--astros-orange)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{s.badge}</span>
                  </div>
                  <h3 className="authority-card__title" style={{ fontSize: '1.3rem' }}>{s.title}</h3>
                  <p className="authority-card__text">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 24/7 CTA */}
      <section style={{ padding: '5rem 2rem', background: 'var(--surface)', textAlign: 'center', borderTop: '1px solid var(--border-dim)' }}>
        <Reveal>
          <div style={{ maxWidth: 600, margin: '0 auto' }}>
            <AlertCircle size={48} style={{ color: 'var(--astros-orange)', marginBottom: '1.5rem' }} />
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 5vw, 3.5rem)', color: 'white', textTransform: 'uppercase', marginBottom: '1rem' }}>
              {lang === 'es' ? 'DISPONIBLES 24/7' : "WE'RE AVAILABLE 24/7"}
            </h2>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem', fontSize: '1.05rem', lineHeight: 1.7 }}>
              {lang === 'es' ? 'Un arresto puede ocurrir en cualquier momento. Respondemos de inmediato, a cualquier hora.' : 'An arrest can happen at any hour. We respond immediately, any time of day or night.'}
            </p>
            <a href="tel:7132288800" className="btn-emergency" data-testid="services-call-btn">
              <Phone size={22} /> (713) 228-8800
            </a>
          </div>
        </Reveal>
      </section>
      <SharedFooter />
    </>
  );
};

export default ServicesPage;
